import { invariantResponse } from '@epic-web/invariant'
import {
	data,
	type LoaderFunctionArgs,
	type MetaFunction,
	Link,
	useLoaderData,
} from 'react-router'
import {
	getLatestContentRevision,
	listContentRevisions,
} from '#app/utils/content-revision.server.ts'
import { prisma } from '#app/utils/db.server.js'
import { requireFestivalVisible } from '#app/utils/maintenance.server.ts'
import { formatDate } from '#app/domains/program/pure'
import { pageMeta } from '#app/utils/page-meta.ts'

export const meta: MetaFunction<typeof loader> = (args) => {
	const performer = args.loaderData?.performer
	return pageMeta(args, {
		title: performer
			? `${performer.name} - endringshistorikk`
			: 'Endringshistorikk',
	})
}

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
	await requireFestivalVisible(request)
	const performer = await prisma.performer.findUnique({
		where: { slug: params.slug },
		select: { id: true, name: true, slug: true },
	})
	invariantResponse(performer, 'Fann ikkje artisten', { status: 404 })
	const [revisions, latest] = await Promise.all([
		listContentRevisions(performer.id),
		getLatestContentRevision(performer.id),
	])
	return data({ performer, revisions, latest })
}

export default function PerformerHistory() {
	const { performer, revisions, latest } = useLoaderData<typeof loader>()

	return (
		<main className="font-poppins grid animate-slide-top place-items-center p-4 pb-16">
			<div className="w-full max-w-3xl">
				<div className="breadcrumbs w-full self-start text-sm">
					<ul>
						<li>
							<Link to="/performers">Artistar</Link>
						</li>
						<li>
							<Link to={`/performers/${performer.slug}`}>{performer.name}</Link>
						</li>
						<li className="font-bold">Endringshistorikk</li>
					</ul>
				</div>
				<h1 className="mb-6 mt-6 text-2xl font-bold">
					Endringshistorikk – {performer.name}
				</h1>
				{latest && (
					<p className="mb-6 text-sm text-base-content/70">
						Sist endra av {latest.editedByName ?? 'ukjend'}{' '}
						{formatDate(latest.createdAt)}
					</p>
				)}
				<ul className="flex flex-col gap-3">
					{revisions.map((revision) => (
						<li key={revision.id} className="rounded-xl bg-base-200 p-4">
							<p className="font-bold">{revision.editedByName ?? 'Ukjent'}</p>
							<p className="capitalize text-base-content/70">
								{formatDate(revision.createdAt)}
							</p>
							<details className="mt-2 text-sm">
								<summary className="label-text cursor-pointer">
									Innhaldet i denne endringsrunden
								</summary>
								<ul className="mt-2 text-base-content/80">
									{Object.entries(
										JSON.parse(revision.data) as Record<string, unknown>,
									).map(([key, value]) => (
										<li key={key}>
											<span className="font-bold">{key}:</span>{' '}
											{value === null || value === ''
												? '–'
												: typeof value === 'string'
													? value.slice(0, 500)
													: String(value)}
										</li>
									))}
								</ul>
							</details>
						</li>
					))}
					{revisions.length === 0 && (
						<li className="text-base-content/70">
							Ingen registrerte endringar enno.
						</li>
					)}
				</ul>
			</div>
		</main>
	)
}
